/**
 * API Logger Utility
 *
 * Provides development-only logging for API requests and responses
 * with automatic sanitization of sensitive data
 */
const isDevelopment = process.env.NODE_ENV === 'development';
/**
 * Sensitive fields that should be sanitized in logs
 */
const SENSITIVE_FIELDS = [
    'password',
    'token',
    'access_token',
    'refresh_token',
    'authorization',
    'csrf-token',
    'x-csrf-token',
    'api_key',
    'apiKey',
    'secret',
    'credit_card',
    'card_number',
    'cvv',
    'ssn',
    'social_security_number'
];
/**
 * Sanitize object by removing or masking sensitive fields
 */
const sanitizeData = (data, depth = 0) => {
    // Prevent infinite recursion
    if (depth > 10) {
        return '[Max depth reached]';
    }
    if (data === null || data === undefined) {
        return data;
    }
    if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
        return data;
    }
    if (Array.isArray(data)) {
        return data.map(item => sanitizeData(item, depth + 1));
    }
    if (typeof data === 'object') {
        const sanitized = {};
        for (const [key, value] of Object.entries(data)) {
            const lowerKey = key.toLowerCase();
            const isSensitive = SENSITIVE_FIELDS.some(field => lowerKey.includes(field.toLowerCase()));
            if (isSensitive) {
                sanitized[key] = '[REDACTED]';
            }
            else {
                sanitized[key] = sanitizeData(value, depth + 1);
            }
        }
        return sanitized;
    }
    return data;
};
/**
 * Sanitize headers object
 */
const sanitizeHeaders = (headers) => {
    if (!headers || typeof headers !== 'object') {
        return headers;
    }
    const sanitized = {};
    for (const [key, value] of Object.entries(headers)) {
        const lowerKey = key.toLowerCase();
        const isSensitive = SENSITIVE_FIELDS.some(field => lowerKey.includes(field.toLowerCase()));
        if (isSensitive) {
            sanitized[key] = '[REDACTED]';
        }
        else {
            sanitized[key] = value;
        }
    }
    return sanitized;
};
/**
 * Log API request (development only)
 */
export const logApiRequest = (config) => {
    if (!isDevelopment)
        return;
    const logData = {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        fullUrl: `${config.baseURL || ''}${config.url || ''}`,
        headers: sanitizeHeaders(config.headers),
        params: sanitizeData(config.params),
        data: sanitizeData(config.data),
        timestamp: new Date().toISOString()
    };
    console.group(`🚀 API Request: ${logData.method} ${logData.url}`);
    console.log('Full URL:', logData.fullUrl);
    console.log('Headers:', logData.headers);
    if (logData.params)
        console.log('Params:', logData.params);
    if (logData.data)
        console.log('Data:', logData.data);
    console.log('Timestamp:', logData.timestamp);
    console.groupEnd();
};
/**
 * Log API response (development only)
 */
export const logApiResponse = (response) => {
    if (!isDevelopment)
        return;
    const logData = {
        status: response.status,
        statusText: response.statusText,
        url: response.config?.url,
        fullUrl: `${response.config?.baseURL || ''}${response.config?.url || ''}`,
        headers: sanitizeHeaders(response.headers),
        data: sanitizeData(response.data),
        timing: response.config?.metadata?.timing,
        timestamp: new Date().toISOString()
    };
    const isSuccess = response.status >= 200 && response.status < 400;
    const logMethod = isSuccess ? console.group : console.groupCollapsed;
    const emoji = isSuccess ? '✅' : '⚠️';
    logMethod(`${emoji} API Response: ${logData.status} ${response.config?.method?.toUpperCase()} ${logData.url}`);
    console.log('Status:', `${logData.status} ${logData.statusText}`);
    console.log('Full URL:', logData.fullUrl);
    if (logData.data)
        console.log('Data:', logData.data);
    if (logData.timing)
        console.log('Timing:', logData.timing);
    console.log('Timestamp:', logData.timestamp);
    console.groupEnd();
};
/**
 * Log API error (development only)
 */
export const logApiError = (error, context) => {
    if (!isDevelopment)
        return;
    const logData = {
        message: error.message,
        code: error.code,
        name: error.name,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        fullUrl: `${error.config?.baseURL || ''}${error.config?.url || ''}`,
        method: error.config?.method?.toUpperCase(),
        responseData: error.response?.data ? sanitizeData(error.response.data) : undefined,
        requestData: error.config?.data ? sanitizeData(error.config.data) : undefined,
        headers: error.config?.headers ? sanitizeHeaders(error.config.headers) : undefined,
        isNetworkError: !error.response && error.request,
        context: context ? sanitizeData(context) : undefined,
        timestamp: new Date().toISOString()
    };
    console.group(`❌ API Error: ${logData.method} ${logData.url}`);
    console.error('Error:', logData.message);
    if (logData.code)
        console.error('Code:', logData.code);
    if (logData.status)
        console.error('Status:', `${logData.status} ${logData.statusText}`);
    console.error('Full URL:', logData.fullUrl);
    if (logData.isNetworkError)
        console.error('Type: Network Error (no response from server)');
    if (logData.responseData)
        console.error('Response Data:', logData.responseData);
    if (logData.requestData)
        console.error('Request Data:', logData.requestData);
    if (logData.context)
        console.error('Context:', logData.context);
    console.error('Timestamp:', logData.timestamp);
    console.groupEnd();
};
/**
 * Create timing metadata for requests
 */
export const createTimingMetadata = () => {
    return {
        startTime: Date.now()
    };
};
/**
 * Calculate request timing
 */
export const calculateTiming = (startTime) => {
    return Date.now() - startTime;
};
